import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import {
  AdminCompanyDetailOutDTO,
  CompanyModuleEnum,
  ComputeReservationDurationConfiguration,
  CounterPartyDefaultActionTypeEnum,
  ExternalOrdersApiTypeEnum,
  FeaturesEnum,
  FlagSetting,
  LovDTO,
  OwnerDefaultActionTypeEnum,
  ReservationFlagTypeEnum,
  UpsertTimeslotModuleSettingRequestInDTO,
} from '@api/ringil-api.v2.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { InputNumber } from '@components/framework/input/InputNumber';
import useForm from '@hooks/useForm/useForm.tsx';
import * as yup from 'yup';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import RingilButton from '@components/framework/buttons/RingilButton.tsx';
import InputSKU from '@components/ringil3/Input/InputSKU/InputSKU.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import SvgCoButtonPlus from '@icons/CoButtonPlus.tsx';
import SvgCoButtonDelete from '@icons/CoButtonDelete.tsx';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';

interface ComponentProps {
  companyId: string;
}

const ModuleSettingsTimeslot: React.FC<ComponentProps> = ({ companyId }) => {
  const {
    getCreateEditReservationSettings,
    upsertTimeslotModuleSettings,
    adminGetCompanyDetail,
    adminListInheritingCompaniesByCompany,
  } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const [loadedData, setLoadedData] = useState<ComputeReservationDurationConfiguration>();
  const { showToastMessage } = useContext(ToastContext);
  const [companyData, setCompanyData] = useState<AdminCompanyDetailOutDTO>(null);
  const [companies, setCompanies] = useState<LovDTO[]>([]);

  const form = useForm<UpsertTimeslotModuleSettingRequestInDTO>(yup.object().shape({}), null, v => save(v), false, false);

  const save = (data: UpsertTimeslotModuleSettingRequestInDTO) => {
    upsertTimeslotModuleSettings(companyId, data, () => {
      showToastMessage('Saved', 'Timeslots settings saved', 'success');
    });
};

  useEffect(() => {
    adminGetCompanyDetail(companyId, data => {
      setCompanyData(data);
    });
    getCreateEditReservationSettings(companyId, d => {
      setLoadedData(d);
      const loadedFlags = (d.flags ?? {}) as Partial<Record<ReservationFlagTypeEnum, FlagSetting>>;
      const flags = Object.values(ReservationFlagTypeEnum).reduce(
        (acc, flag) => {
          acc[flag] = loadedFlags[flag] ?? { visible: false, visibleAdditionalField: false, additionalFieldRequired: false };
          return acc;
        },
        {} as Record<ReservationFlagTypeEnum, FlagSetting>,
      );
      form.overwriteValues({
        units: d.units.map(u => ({ cargoItemType: u.cargoItemType, duration: u.duration })),
        ...d,
        nextDayReservationJustBeforeExcept: d.nextDayReservationJustBeforeExceptCompanies,
        nextDayReservationJustBeforeExceptCompanyIds: d.nextDayReservationJustBeforeExceptCompaniesList?.map(c => c.id) || [],
        flags,
      });
    });
    adminListInheritingCompaniesByCompany(companyId, data => {
      const timeslotCompanies = data
        .filter(c => c.companyModule === CompanyModuleEnum.TIMESLOTS)
        .map(c => ({
          id: c.companyId,
          name: c.companyName,
          active: true,
        }));
      setCompanies(timeslotCompanies);
    });
  }, []);

  const defaultActionTypeOptions = [
    { value: OwnerDefaultActionTypeEnum.NONE, label: tr('ReservationDefaultActionTypeEnum.NONE', 'None') },
    { value: OwnerDefaultActionTypeEnum.LOADING, label: tr('ReservationDefaultActionTypeEnum.LOADING', 'Loading') },
    { value: OwnerDefaultActionTypeEnum.UNLOADING, label: tr('ReservationDefaultActionTypeEnum.UNLOADING', 'Unloading') },
    { value: OwnerDefaultActionTypeEnum.BOTH, label: tr('ReservationDefaultActionTypeEnum.BOTH', 'Loading and unloading') },
  ];

  const add = () => {
    form.setFieldValue(form.names.units, [...form.values.units, { cargoItemType: '', duration: 0 }]);
  };

  const deleteHandler = (index: number) => {
    form.setFieldValue(
      form.names.units,
      form.values.units.filter((_, i) => i !== index),
    );
  };

  if (isNullOrUndefined(form?.values)) return <></>;

  return (
    <>
      <div>
        <BreadCrumbComponent items={[{ label: 'Timeslot module settings' }, { label: loadedData?.companyName }]} />
        <COHeadline title={tr('ModuleSettingsTimeslot.timeslotModuleSettings', 'Timeslot module settings')}
                    variant={'h2'} />
        <div className={'flex flex-col gap-4 w-[500px]'}>
          <Field label={tr('ModuleSettingsTimeslot.defaultDuration', 'Default duration')} required>
            <InputNumber value={form.values.defaultDuration}
                         onChange={e => form.setFieldValue(form.names.defaultDuration, e.value)} />
          </Field>
          <Field label={tr('ModuleSettingsTimeslot.minimalDuration', 'Minimal duration')} required>
            <InputNumber value={form.values.minimalDuration}
                         onChange={e => form.setFieldValue(form.names.minimalDuration, e.value)} />
          </Field>
          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.editingReservation', 'Editing reservation')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.ownerCanEdit}
            onChange={v => form.setFieldValue(form.names.ownerCanEdit, v)}
            label={tr('ModuleSettingsTimeslot.ownerCanEdit', 'Owner can edit')}
          />
          <RingilInputSwitch
            value={form.values.counterPartyCanEdit}
            onChange={v => form.setFieldValue(form.names.counterPartyCanEdit, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyCanEdit', 'Counter party can edit')}
          />
          <Field
            label={tr('ModuleSettingsTimeslot.counterPartyEditBlockedMinutesBeforeStart', 'Counter party edit blocked minutes before start')}>
            <InputNumber
              value={form.values.counterPartyEditBlockedMinutesBeforeStart}
              onChange={e => form.setFieldValue(form.names.counterPartyEditBlockedMinutesBeforeStart, e.value)}
              min={0}
            />
          </Field>

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.carrierCalendar', 'Carrier calendar')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.showCarrierAnonymousReservations}
            onChange={v => form.setFieldValue(form.names.showCarrierAnonymousReservations, v)}
            label={tr(
              'ModuleSettingsTimeslot.showCarrierAnonymousReservations',
              "Show other carriers' reservations as anonymous",
            )}
          />

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.requiredSlots', 'Required slots')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.slotsRequired}
            onChange={v => form.setFieldValue(form.names.slotsRequired, v)}
            label={tr('ModuleSettingsTimeslot.ownerHaveToChooseSlots', 'Owner have to choose slots')}
          />
          <RingilInputSwitch
            value={form.values.slotsRequiredCounterParty}
            onChange={v => form.setFieldValue(form.names.slotsRequiredCounterParty, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyHaveToChooseSlots', 'Counter party have to choose slots')}
          />

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.editDuration', 'Edit duration')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.editDuration}
            onChange={v => form.setFieldValue(form.names.editDuration, v)}
            label={tr('ModuleSettingsTimeslot.ownerCanEditDuration', 'Owner can edit duration')}
          />
          <RingilInputSwitch
            value={form.values.editDurationCounterParty}
            onChange={v => form.setFieldValue(form.names.editDurationCounterParty, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyCanChangeEditDuration', 'Counter party can change edit duration')}
          />

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.offerHistoricalSlots', 'Offer historical slots')}
                      variant={'h3'} />
          <RingilInputSwitch
            value={form.values.showHistoricalFreeSlots}
            onChange={v => form.setFieldValue(form.names.showHistoricalFreeSlots, v)}
            label={tr('ModuleSettingsTimeslot.ownerCanChooseHistoricalSlots', 'Owner can choose historical slots')}
          />

          <RingilInputSwitch
            value={form.values.showHistoricalFreeSlotsCounterParty}
            onChange={v => form.setFieldValue(form.names.showHistoricalFreeSlotsCounterParty, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyCanChooseHistoricalSlots', 'Counter party can choose historical slots')}
          />

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.restrictToExpeditionHours', 'Restrict slots to expedition opening hours')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.restrictToExpeditionHoursOwner}
            onChange={v => form.setFieldValue(form.names.restrictToExpeditionHoursOwner, v)}
            label={tr('ModuleSettingsTimeslot.ownerRestrictToExpeditionHours', 'Owner sees only slots within expedition opening hours')}
          />
          <RingilInputSwitch
            value={form.values.restrictToExpeditionHoursCounterParty}
            onChange={v => form.setFieldValue(form.names.restrictToExpeditionHoursCounterParty, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyRestrictToExpeditionHours', 'Counter party sees only slots within expedition opening hours')}
          />

          <HorizontalLine variant={'tertiary'} />
          <div className={'flex flex-row justify-between items-center'}>
            <COHeadline title={tr('ModuleSettingsTimeslot.units', 'Units')} variant={'h3'} />
            <RingilButton mode={'rect-small'} leftIcon={<SvgCoButtonPlus />} onClick={add} />
          </div>
          {form.values?.units?.map((unit, unitIndex) => {
            return (
              <div className={'grid grid-cols-2 gap-3'} key={unitIndex}>
                <Field label={tr('ModuleSettingsTimeslot.manipulationUnit', 'Manipulation unit')} required>
                  <InputSKU
                    fullWidth
                    value={unit.cargoItemType}
                    companyId={companyId}
                    onChange={v => form.setFieldValue(`units[${unitIndex}].cargoItemType`, v)}
                  />
                </Field>
                <Field
                  label={tr('ModuleSettingsTimeslot.timeForManipulationUnit', 'Time for manipulation unit')}
                  required
                  actionButtons={<RingilButton mode={'rect-small'} leftIcon={<SvgCoButtonDelete />}
                                               onClick={() => deleteHandler(unitIndex)} />}
                >
                  <InputNumber value={unit.duration} maxFractionDigits={2}
                               onChange={e => form.setFieldValue(`units[${unitIndex}].duration`, e.value)} />
                </Field>
              </div>
            );
          })}

          <COHeadline title={tr('ModuleSettingsTimeslot.requiredOrders', 'Required orders')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.ownerManipulationItemsRequired}
            onChange={v => form.setFieldValue(form.names.ownerManipulationItemsRequired, v)}
            label={tr('ModuleSettingsTimeslot.ownerHaveToAddOrders', 'Owner have to add orders')}
          />

          <RingilInputSwitch
            value={form.values.counterPartyManipulationItemsRequired}
            onChange={v => form.setFieldValue(form.names.counterPartyManipulationItemsRequired, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyHaveToAddOrders', 'Counter party have to add orders')}
          />

          <COHeadline title={tr('ModuleSettingsTimeslot.generalOrderSettings', 'General orders setting')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.doubleLoadingUnloadingTime}
            onChange={v => form.setFieldValue(form.names.doubleLoadingUnloadingTime, v)}
            label={tr('ModuleSettingsTimeslot.doubleLoadingUnloadingTime', 'Double reservation time for loading/unloading')}
          />

          {companyData?.features?.includes(FeaturesEnum.RESERVATION_TRUCK_TYPE) && (
            <>
              <HorizontalLine variant={'tertiary'} />
              <COHeadline title={tr('ModuleSettingsTimeslot.truckType', 'Truck type')} variant={'h3'} />
              <RingilInputSwitch
                value={form.values.truckTypeRequired}
                onChange={v => form.setFieldValue(form.names.truckTypeRequired, v)}
                label={tr('ModuleSettingsTimeslot.truckTypeRequired', 'Truck type is required')}
              />
            </>
          )}

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.carrierDetails', 'Carrier details')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.driverNameRequired}
            onChange={v => form.setFieldValue(form.names.driverNameRequired, v)}
            label={tr('ModuleSettingsTimeslot.ownerDriverName', 'Owner have to fill driver')}
          />
          <RingilInputSwitch
            value={form.values.driverNameRequiredCounterParty}
            onChange={v => form.setFieldValue(form.names.driverNameRequiredCounterParty, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyDriverName', 'Counter party have to fill driver')}
          />
          <RingilInputSwitch
            value={form.values.driverPhoneRequired}
            onChange={v => form.setFieldValue(form.names.driverPhoneRequired, v)}
            label={tr('ModuleSettingsTimeslot.ownerDriverPhone', 'Owner have to fill driver phone')}
          />
          <RingilInputSwitch
            value={form.values.driverPhoneRequiredCounterParty}
            onChange={v => form.setFieldValue(form.names.driverPhoneRequiredCounterParty, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyDriverPhone', 'Counter party have to fill driver phone')}
          />
          <RingilInputSwitch
            value={form.values.driverIdRequired}
            onChange={v => form.setFieldValue(form.names.driverIdRequired, v)}
            label={tr('ModuleSettingsTimeslot.ownerDriverId', 'Owner have to fill driver ID')}
          />
          <RingilInputSwitch
            value={form.values.driverIdRequiredCounterParty}
            onChange={v => form.setFieldValue(form.names.driverIdRequiredCounterParty, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyDriverId', 'Counter party have to fill driver ID')}
          />
          <RingilInputSwitch
            value={form.values.carPlateRequired}
            onChange={v => form.setFieldValue(form.names.carPlateRequired, v)}
            label={tr('ModuleSettingsTimeslot.ownerCarPlate', 'Owner have to fill car plate')}
          />
          <RingilInputSwitch
            value={form.values.carPlateRequiredCounterParty}
            onChange={v => form.setFieldValue(form.names.carPlateRequiredCounterParty, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyCarPlate', 'Counter party have to fill car plate')}
          />

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.nextDayReservation', 'Next day reservation')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.nextDayReservationJustBefore}
            onChange={v => {
              form.setFieldValue(form.names.nextDayReservationJustBefore, v);
              if (!v) {
                form.setFieldValue(form.names.nextDayReservationJustBeforeHours, null);
              }
            }}
            label={tr('ModuleSettingsTimeslot.nextDayReservationJustBefore', 'Next day reservation just before')}
          />
          {form.values.nextDayReservationJustBefore && (
            <>
              <Field label={tr('ModuleSettingsTimeslot.nextDayReservationJustBeforeHours', 'Hours before')}>
                <InputNumber
                  value={form.values.nextDayReservationJustBeforeHours}
                  onChange={e => form.setFieldValue(form.names.nextDayReservationJustBeforeHours, e.value)}
                  min={0}
                  max={23}
                />
              </Field>
              <RingilInputSwitch
                value={form.values.nextDayReservationJustBeforeExcept}
                onChange={v => {
                  form.setFieldValue(form.names.nextDayReservationJustBeforeExcept, v);
                  if (!v) {
                    form.setFieldValue(form.names.nextDayReservationJustBeforeExceptCompanyIds, []);
                  }
                }}
                label={tr('ModuleSettingsTimeslot.nextDayReservationJustBeforeExcept', 'Except for selected companies')}
              />
              {form.values.nextDayReservationJustBeforeExcept && (
                <Field label={tr('ModuleSettingsTimeslot.exceptCompanies', 'Companies')}>
                  <MultiSelect
                    value={form.values.nextDayReservationJustBeforeExceptCompanyIds}
                    options={companies}
                    optionLabel={'name'}
                    optionValue={'id'}
                    filter={true}
                    onChange={v => form.setFieldValue(form.names.nextDayReservationJustBeforeExceptCompanyIds, v.value)}
                    placeholder={tr('ModuleSettingsTimeslot.selectCompanies', 'Select companies')}
                  />
                </Field>
              )}
            </>
          )}

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.canDeleteReservation', 'Can delete reservation')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.ownerCanDelete}
            onChange={v => form.setFieldValue(form.names.ownerCanDelete, v)}
            label={tr('ModuleSettingsTimeslot.ownerCanDeleteReservation', 'Owner can delete reservation')}
          />

          <RingilInputSwitch
            value={form.values.counterPartyCanDelete}
            onChange={v => form.setFieldValue(form.names.counterPartyCanDelete, v)}
            label={tr('ModuleSettingsTimeslot.counterCanDeleteReservation', 'Counter party can delete reservation')}
          />

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.showResActionTypes', 'Show reservation action types')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.ownerShowActionTypes}
            onChange={v => {
              form.setFieldValue(form.names.ownerShowActionTypes, v);
              if (!v) {
                form.setFieldValue(form.names.ownerDefaultActionType, OwnerDefaultActionTypeEnum.NONE);
                form.setFieldValue(form.names.ownerActionTypeRequired, false);
              }
            }}
            label={tr('ModuleSettingsTimeslot.ownerCanSeeActionTypes', 'Owner can see action types')}
          />
          <RingilInputSwitch
            value={form.values.ownerActionTypeRequired}
            disabled={!form.values.ownerShowActionTypes}
            onChange={v => form.setFieldValue(form.names.ownerActionTypeRequired, v)}
            label={tr('ModuleSettingsTimeslot.ownerActionTypeRequired', 'Customer must select handling type')}
          />
          <Field label={tr('ModuleSettingsTimeslot.ownerDefaultActionType', 'Customer default handling type')} disabled={!form.values.ownerShowActionTypes}>
            <Dropdown
              value={form.values.ownerDefaultActionType}
              options={defaultActionTypeOptions}
              onChange={e => form.setFieldValue(form.names.ownerDefaultActionType, e.value)}
            />
          </Field>

          <RingilInputSwitch
            value={form.values.counterPartyShowActionTypes}
            onChange={v => {
              form.setFieldValue(form.names.counterPartyShowActionTypes, v);
              if (!v) {
                form.setFieldValue(form.names.counterPartyDefaultActionType, CounterPartyDefaultActionTypeEnum.NONE);
                form.setFieldValue(form.names.counterPartyActionTypeRequired, false);
              }
            }}
            label={tr('ModuleSettingsTimeslot.counterPartyCanSeeActionTypes', 'Counter party can see action types')}
          />
          <RingilInputSwitch
            value={form.values.counterPartyActionTypeRequired}
            disabled={!form.values.counterPartyShowActionTypes}
            onChange={v => form.setFieldValue(form.names.counterPartyActionTypeRequired, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyActionTypeRequired', 'Counterparty must select handling type')}
          />
          <Field label={tr('ModuleSettingsTimeslot.counterPartyDefaultActionType', 'Counterparty default handling type')} disabled={!form.values.counterPartyShowActionTypes}>
            <Dropdown
              value={form.values.counterPartyDefaultActionType}
              options={defaultActionTypeOptions}
              onChange={e => form.setFieldValue(form.names.counterPartyDefaultActionType, e.value)}
            />
          </Field>

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.showWorkflowStatus', 'Show reservation status')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.counterPartyShowWorkflowStatus}
            onChange={v => form.setFieldValue(form.names.counterPartyShowWorkflowStatus, v)}
            label={tr('ModuleSettingsTimeslot.counterPartyShowWorkflowStatus', 'Counter party can see reservation status')}
          />

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.reservationFlags', 'Reservation flags')} variant={'h3'} />
          {Object.values(ReservationFlagTypeEnum).map(flag => {
            const flagValues: FlagSetting = form.values.flags?.[flag] ?? { visible: false, visibleAdditionalField: false, additionalFieldRequired: false };
            return (
              <div key={flag} className={'flex flex-col gap-2 pl-2 border-l-2 border-content-tertiary'}>
                <COHeadline title={tr(`ModuleSettingsTimeslot.flag.${flag}`, flag)} variant={'h4'} />
                <RingilInputSwitch
                  value={flagValues.visible}
                  onChange={v => {
                    form.setFieldValue(`flags.${flag}.visible`, v);
                    if (!v) {
                      form.setFieldValue(`flags.${flag}.visibleAdditionalField`, false);
                      form.setFieldValue(`flags.${flag}.additionalFieldRequired`, false);
                    }
                  }}
                  label={tr('ModuleSettingsTimeslot.flagVisible', 'Visible')}
                />
                <RingilInputSwitch
                  value={flagValues.visibleAdditionalField}
                  disabled={!flagValues.visible}
                  onChange={v => {
                    form.setFieldValue(`flags.${flag}.visibleAdditionalField`, v);
                    if (!v) {
                      form.setFieldValue(`flags.${flag}.additionalFieldRequired`, false);
                    }
                  }}
                  label={tr('ModuleSettingsTimeslot.flagVisibleAdditionalField', 'Show additional field')}
                />
                <RingilInputSwitch
                  value={flagValues.additionalFieldRequired}
                  disabled={!flagValues.visible}
                  onChange={v => {
                    form.setFieldValue(`flags.${flag}.additionalFieldRequired`, v);
                    if (v && !flagValues.visibleAdditionalField) {
                      form.setFieldValue(`flags.${flag}.visibleAdditionalField`, true);
                    }
                  }}
                  label={tr('ModuleSettingsTimeslot.flagAdditionalFieldRequired', 'Additional field required')}
                />
              </div>
            );
          })}

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.folders', 'Folders')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.foldersRequired === true}
            onChange={v => form.setFieldValue(form.names.foldersRequired, v)}
            label={tr('ModuleSettingsTimeslot.foldersRequired', 'Folder is required when the owner creates a reservation')}
          />
          <RingilInputSwitch
            value={form.values.foldersRestrictVisibility === true}
            onChange={v => form.setFieldValue(form.names.foldersRestrictVisibility, v)}
            label={tr('ModuleSettingsTimeslot.foldersRestrictVisibility', 'Owner users only see reservations in folders they have access to')}
          />

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.noShowScheduler', 'No show scheduler')} variant={'h3'} />
          <RingilInputSwitch
            value={form.values.noShowSchedulerEnabled}
            onChange={v => form.setFieldValue(form.names.noShowSchedulerEnabled, v)}
            label={tr('ModuleSettingsTimeslot.noShowSchedulerEnabled', 'No show scheduler enabled')}
          />

          <HorizontalLine variant={'tertiary'} />
          <COHeadline title={tr('ModuleSettingsTimeslot.externalOrdersApi', 'External orders API')} variant={'h3'} />
          <Field label={tr('ModuleSettingsTimeslot.externalOrdersApiType', 'External orders API type')}>
            <Dropdown
              value={form.values.externalOrdersApiType}
              options={Object.values(ExternalOrdersApiTypeEnum).map(v => ({
                label: tr(`ExternalOrdersApiTypeEnum.${v}`, v),
                value: v,
              }))}
              onChange={e => form.setFieldValue(form.names.externalOrdersApiType, e.value)}
              placeholder={tr('ModuleSettingsTimeslot.selectExternalOrdersApiType', 'Select API type')}
              showClear
            />
          </Field>

          <RingilButton label={tr('ModuleSettingsTimeslot.save', 'Save')} onClick={() => form.validateAndSend()} />
        </div>
      </div>
    </>
  );
};

export default ModuleSettingsTimeslot;
